import React from 'react';
import type {
  ColDef,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getRolePermissions,
  saveBatchRolePermissions,
} from '../../api/rolePermissions';
import type { RolePermission } from '../../types/RolePermission';
import { t } from '../../i18n/t';

// permissionLevel is stored 0/1/2 but shown/edited as Hebrew labels.
// Index === level, so indexOf reverses the mapping.
const LEVEL_LABELS = [
  t('rolePermissions.levels.none'), // 0
  t('rolePermissions.levels.view'), // 1
  t('rolePermissions.levels.edit'), // 2
];

const rolePermissionsColumnDefs: ColDef<RolePermission>[] = [
  {
    field: 'roleName',
    headerName: t('rolePermissions.columns.roleName'),
    flex: 1,
    editable: false,
  },
  {
    field: 'resourceDescription',
    headerName: t('rolePermissions.columns.resourceDescription'),
    flex: 1,
    editable: false,
  },
  {
    field: 'permissionLevel',
    headerName: t('rolePermissions.columns.permissionLevel'),
    width: 160,
    // editable inherits LopCrudPage's defaultColDef.editable (= canEdit on the
    // resource); no explicit callback needed.
    cellDataType: 'text', // per CLAUDE.md dropdown rule (number field, string UI)
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: { values: LEVEL_LABELS },
    valueGetter: (p: ValueGetterParams<RolePermission>) => {
      const lvl = p.data?.permissionLevel ?? 0;
      return LEVEL_LABELS[lvl] ?? LEVEL_LABELS[0];
    },
    valueSetter: (p: ValueSetterParams<RolePermission>) => {
      if (!p.data) return false;
      const idx = LEVEL_LABELS.indexOf(p.newValue);
      if (idx === -1) return false;
      p.data.permissionLevel = idx;
      return true;
    },
  },
];

export function RolePermissionsPage() {
  return (
    <LopCrudPage<RolePermission>
      resourceCode="rolePermissions"
      titleKey="rolePermissions.pageTitle"
      idField="roleId"
      getRows={getRolePermissions}
      saveBatch={(_added, modified) =>
        // Update-only: ignore added/deleted (toolbar hides Add/Delete). Map
        // the changed full rows to the minimal update DTO.
        saveBatchRolePermissions(
          modified.map((r) => ({
            roleId: r.roleId,
            resourceId: r.resourceId,
            permissionLevel: r.permissionLevel,
          })),
        )
      }
      columnDefs={rolePermissionsColumnDefs}
      showAdd={false}
      showDelete={false}
    />
  );
}
